import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { RfrStepProps } from "../../../pages/FileRFR/RfRForm";
import AmpButton from "../../common/AmpButton/AmpButton";
import AmpFormError from "../../common/AmpFormError/AmpFormError";
import RfrMultipleCheckboxes from "./RfrMultipleCheckboxes";
import RfrPoiScroller from "./RfrPoiScroller";
import RfrUploadFiles from "./RfrUploadFiles";

import "./Steps.css";


export default function Step3(props: RfrStepProps) {
    const { t, i18n } = useTranslation("rfr");

    const step31ReasonInputOptions = [
        {
            value: "INCORRECT/MISSING PROPERTY INFORMATION",
            labelkey: "rfr.rfrform.step3.reason-propinfo"
        },
        {
            value: "ASSESSED VALUE TOO HIGH",
            labelkey: "rfr.rfrform.step3.reason-toohigh"
        },
        {
            value: "ASSESSED VALUE TOO LOW",
            labelkey: "rfr.rfrform.step3.reason-toolow"
        },
        {
            value: "INCORRECT CLASSIFICATION",
            labelkey: "rfr.rfrform.step3.reason-classification"
        },
        {
            value: "OTHER",
            labelkey: "rfr.rfrform.step3.reason-other"
        }
    ];

    const step32DocInputOptions = [
        {
            value: "SALE INFORMATION WITH THIS PROPERTY AND OTHER SIMILAR PROPERTIES",
            labelkey: "rfr.rfrform.step3.doc-sale"
        },
        {
            value: "ASSESSED VALUE OF SIMILAR PROPERTIES",
            labelkey: "rfr.rfrform.step3.doc-value"
        },
        {
            value: "PHOTOS OF THIS PROPERTY / OTHER SIMILAR PROPERTIES",
            labelkey: "rfr.rfrform.step3.doc-photo"
        },
        {
            value: "OTHER DOCUMENTATION, SUCH AS MUNICIPAL ZONING RECORD",
            labelkey: "rfr.rfrform.step3.doc-other"
        }
    ];

    const formdata = props.formdata;
    const pois = props.statedata.pois;

    const [stepdata, setStepdata] = useState({
        step31ReasonAreaInput: "step31ReasonAreaInput" in formdata ? formdata["step31ReasonAreaInput"] : "",
        step31ReasonInput: "step31ReasonInput" in formdata ? formdata["step31ReasonInput"] : [],
        step32DocInput: "step32DocInput" in formdata ? formdata["step32DocInput"] : [],
        pois: "pois" in formdata ? formdata["pois"] : pois.map(({ poi_roll_num }) => poi_roll_num)
    });
    const [files, setFiles] = useState("step32FileInput" in formdata ? formdata["step32FileInput"] : []);
    const [errors, setErrors] = useState({
        step31ReasonInput: "",
        step31ReasonAreaInput: "",
        step32FileInput: "",
    });

    const clearErrors = () => {
        Object.keys(errors).forEach((err) => {
            errors[err] = "";
        });
        setErrors(prevState => ({
            ...prevState,
            ...errors
        }));
    }

    const clearStepdata = (e) => {
        e.preventDefault();
        clearErrors();
        stepdata.step31ReasonAreaInput = "";
        stepdata.step31ReasonInput = [];
        stepdata.step32DocInput = [];
        stepdata.pois = pois.map(({ poi_roll_num }) => poi_roll_num);
        setStepdata(prevState => ({
            ...prevState,
            ...stepdata
        }));
        setFiles([]);
    }

    const trimData = () => {
        Object.keys(stepdata).forEach((key) => {
            const value = stepdata[key];
            if (typeof value === "string") {
                stepdata[key] = value.trim();
            }
        });
        setStepdata(prevState => ({
            ...prevState,
            ...stepdata
        }));
    }

    const setData = (name: string, value: string | Array<string>) => {
        if (formdata.disableSubmit) {
            return;
        }
        stepdata[name] = value;
        setStepdata(prevState => ({
            ...prevState,
            ...stepdata
        }));
    }

    const handleInputData = (event) => {
        const { name, value } = event.target;
        setData(name, value);
    }

    const validate = () => {
        clearErrors();
        trimData();

        let valid = true;
        if (stepdata.step31ReasonInput.length === 0) {
            valid = false;
            errors["step31ReasonInput"] = t("rfr.rfrform.step3.error-reason-required");
        }
        if (stepdata.step31ReasonAreaInput.length === 0 || stepdata.step31ReasonAreaInput.length > 1200) {
            valid = false;
            errors["step31ReasonAreaInput"] = t("rfr.rfrform.step3.error-reason");
        }
        if (stepdata.step32DocInput.length > 0 && files.length === 0) {
            valid = false;
            errors["step32FileInput"] = t("rfr.rfrform.step3.error-doc");
        }

        setErrors(errors);
        return valid;
    }

    const findFirstError = () => {
        let firstError = null;
        Object.keys(errors).forEach((err) => {
            if (errors[err] !== "" && firstError === null) {
                let elems = document.getElementsByName(err);
                if (elems === null || elems === undefined || elems.length === 0) {
                    elems = document.getElementsByName(err + "First");
                }
                if (elems !== null && elems !== undefined && elems.length > 0) {
                    firstError = elems[0];
                }
            }
        });
        return firstError;
    }

    const handleNext = (e) => {
        const valid = validate();
        if (!valid) {
            const firstError = findFirstError();
            if (firstError !== null && firstError !== undefined) {
                firstError.scrollIntoView({ behavior: "smooth", block: "start" });
            }
            e.preventDefault();
            return;
        }
        setData("language", i18n.language);
        setData("step31ReasonInputText", stepdata["step31ReasonInput"].map((reason) => t(reason)));
        props.handleStepChange("submit", stepdata, files, "step32FileInput");
    }

    const handleBack = (e) => {
        e.preventDefault();
        props.handleStepChange("step2", stepdata, files, "step32FileInput");
    }

    return (
        <div>
            <div className="tab-title font_size_13">
                {t("rfr.rfrform.step3.tab-title")}
            </div>
            <div className="form_body">
                <div className="rfr-form-blue-row">
                    {t("rfr.rfrform.step3.reason-title")} *
                </div>
                <RfrMultipleCheckboxes name="step31ReasonInput" options={step31ReasonInputOptions} data={stepdata} setData={setData} />
                <AmpFormError error={errors["step31ReasonInput"] !== ""} errorMessage={t("rfr.rfrform.step3.error-reason-required")} />
                <div className="rfr-form-cont padding-bottom">
                    <div className="rfr-form-row">
                        <div className="rfr-form-row-textarea">
                            <textarea onChange={(e) => handleInputData(e)} value={stepdata.step31ReasonAreaInput} name="step31ReasonAreaInput" id="step31ReasonAreaInput" rows={8} className="width-100" title={t("rfr.rfrform.step3.reason-placeholder")} placeholder={t("rfr.rfrform.step3.reason-placeholder")}></textarea>
                        </div>
                    </div>
                </div>
                <AmpFormError error={errors["step31ReasonAreaInput"] !== ""} errorMessage={t("rfr.rfrform.step3.error-reason")} />

                <div className="rfr-form-blue-row">
                    {t("rfr.rfrform.step3.doc-title-1")}
                    <p>
                        {t("rfr.rfrform.step3.doc-title-2")}
                    </p>
                </div>
                <RfrMultipleCheckboxes name="step32DocInput" options={step32DocInputOptions} data={stepdata} setData={setData} />
                <RfrUploadFiles name="step32FileInput" maxFiles={5} allowedTypes=".jpeg, .jpg, .png, .txt, .doc, .docx, .pdf" maxSizeTotal={3 * 1024 * 1024} maxSizeLabelKey="rfr.rfrform.step3.max-file-size" files={files} setFiles={setFiles} desc="" disabled={formdata.disableSubmit} />
                <AmpFormError error={errors["step32FileInput"] !== ""} errorMessage={t("rfr.rfrform.step3.error-doc")} />

                <div className="rfr-form-blue-row">
                    {t("rfr.rfrform.step3.fav-title-1")}
                    <p>
                        {t("rfr.rfrform.step3.fav-title-2")}
                    </p>
                </div>
                <RfrPoiScroller name="pois" options={pois} data={stepdata} setData={setData} />
            </div>
            <div className="box_footer">
                <div>
                    <Link className="need-help-btn amp-anchor" to="/amp/contact-us" target="_blank" rel="noreferrer noopener">
                        {t("rfr.rfrform.needhelp")}
                    </Link>
                </div>
                <div>
                    <AmpButton onClick={(e) => clearStepdata(e)} text={t("rfr.rfrform.clear")} type="cancel" disabled={formdata.disableSubmit} />
                    <AmpButton onClick={(e) => handleBack(e)} text={t("rfr.rfrform.back")} type="cancel" disabled={formdata.disableSubmit} />
                    <AmpButton onClick={(e) => handleNext(e)} text={t("rfr.rfrform.step3.submit")} type="submit" disabled={formdata.disableSubmit} />
                </div>
            </div>
        </div>
    );
}